import { PrintActions } from './PrintQueue';
import { FulfillmentPanel, fulfillmentLabels } from './FulfillmentPanel';
import { Pagination } from '../shared/Pagination';
import { useEffect, useState, useCallback } from 'react';
import { ScanLine, History, Package, Loader2, User, MapPin, Truck } from 'lucide-react';
import { PackageFilters } from './PackageFilters';
import { ProductCountModal } from './ProductCountModal';
import { BulkActionBar } from './BulkActionBar';
import { PackageHistoryPanel } from './PackageHistoryPanel';
import { ScannerModal } from '../scanner/ScannerModal';
import { ReturnBatchModal } from './ReturnBatchModal';
import { UnbatchedReturnsModal } from './UnbatchedReturnsModal';
import ReturnBatchesModal from '../../../shared/inventory/ReturnBatchesModal';

const STATUS_CONFIG = {
  in_warehouse:  { label: 'En Bodega',     color: 'bg-amber-100 text-amber-700' },
  in_fastmax:    { label: 'En FastMax',    color: 'bg-blue-100 text-blue-700' },
  with_driver:   { label: 'Con Conductor', color: 'bg-purple-100 text-purple-700' },
  delivered:     { label: 'Entregado',     color: 'bg-green-100 text-green-700' },
  returned:      { label: 'Devuelto',      color: 'bg-red-100 text-red-700' },
};

/**
 * PackageTable — Layer 2: Full filterable package table.
 *
 * Features:
 *   - Filter bar (driver, status, district, date range)
 *   - Checkbox selection for bulk status change
 *   - Per-row history panel trigger
 *   - Scanner modal for adding packages
 *   - Pagination
 */
export function PackageTable({
  dashboardData,
  presetFilter,
  historyPackageId,
  setHistoryPackageId,
  scannerOpen,
  setScannerOpen,
  userId, onPrint, onQueue, isQueued,
}) {
  const {
    packages, pagination, packagesLoading, packagesError,
    productCounts, productCountsError, productSort, setProductSort, productPage, setProductPage,
    filters, applyFilter, clearFilters, goToPage,
    history, historyLoading, fetchPackageHistory,
    bulkLoading, bulkUpdateStatus,
    fetchPackages, fetchStats,
    users, usersLoading, activeUsersOnly, setActiveUsersOnly, requestUsers, setUserSearch,
    drivers, driversLoading, driversError, requestDrivers,
  } = dashboardData;

  // Selected row IDs for bulk operations
  const [selectedIds, setSelectedIds] = useState([]);
  const [returnSelection, setReturnSelection] = useState(null);
  const [returnDraft, setReturnDraft] = useState([]);
  const [returnFromDraft, setReturnFromDraft] = useState(false);
  const [unbatchedOpen, setUnbatchedOpen] = useState(false);
  const [returnBatchesOpen, setReturnBatchesOpen] = useState(false);
  const [fulfillmentPackage, setFulfillmentPackage] = useState(null);
  const [productCountOpen, setProductCountOpen] = useState(false);
  useEffect(() => setSelectedIds([]), [filters]);

  // Apply preset filter when drilling down from the overview
  useEffect(() => {
    if (presetFilter) {
      applyFilter(presetFilter);
    }
  }, [presetFilter, applyFilter]);

  // ── Selection helpers ────────────────────────────────────────────────────
  const isSelected = (id) => selectedIds.includes(id);

  const toggleRow = useCallback((id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  }, []);

  const toggleAll = useCallback(() => {
    if (packages.every(p => selectedIds.includes(p.id))) {
      setSelectedIds([]);
    } else {
      setSelectedIds(packages.map(p => p.id));
    }
  }, [selectedIds, packages]);

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  // The ID-entry return draft is independent of table filters and pagination.
  // Only the confirmed batch writes package statuses or persists a manifest.
  const addReturnPackage = useCallback(pkg => {
    if (pkg.in_pending_return_batch) return 'Este paquete ya pertenece a un lote de devolución pendiente.';
    if (!pkg.store_user?.id) return 'El paquete no tiene una tienda asociada.';
    if (returnDraft.some(item => item.id === pkg.id)) return 'Este paquete ya está en la lista.';
    if (returnDraft.length >= 100) return 'Un lote admite como máximo 100 paquetes.';
    if (returnDraft.length && returnDraft[0].store_user.id !== pkg.store_user.id &&
        (!pkg.store_user.provider_id || pkg.store_user.provider_id !== returnDraft[0].store_user.provider_id)) {
      return 'Cada lote debe contener una tienda o tiendas del mismo proveedor.';
    }
    setReturnDraft(prev => [...prev, pkg]);
    return null;
  }, [returnDraft]);

  const prepareReturnDraft = useCallback(() => {
    if (!returnDraft.length) return;
    setReturnFromDraft(true);
    setReturnSelection(returnDraft);
  }, [returnDraft]);

  // ── Bulk action ──────────────────────────────────────────────────────────
  const handleBulkStatus = useCallback(async (newStatus) => {
    if (newStatus === 'returned') {
      setReturnFromDraft(false);
      setReturnSelection(packages.filter(pkg => selectedIds.includes(pkg.id)));
      return;
    }
    try {
      await bulkUpdateStatus(selectedIds, newStatus, userId);
      setSelectedIds([]);
    } catch {
      alert('Error al cambiar el estado. Por favor intente nuevamente.');
    }
  }, [selectedIds, packages, bulkUpdateStatus, userId]);

  // ── Scanner callback: refresh table + stats after a successful scan ──────
  const handleScanSuccess = useCallback(() => {
    fetchPackages();
  }, [fetchPackages]);

  const allSelected = packages.length > 0 && packages.every(p => selectedIds.includes(p.id));
  const someSelected = packages.some(p => selectedIds.includes(p.id)) && !allSelected;

  return (
    <div className="h-full min-w-0 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl min-w-0 mx-auto px-3 sm:px-4 py-4 space-y-3">

          {/* Bulk action bar — floats above table when rows selected */}
          <BulkActionBar
            selectedIds={selectedIds}
            onBulkStatus={handleBulkStatus}
            onClearSelection={clearSelection}
            loading={bulkLoading}
          />

          {/* Title with package count */}
          {!packagesLoading && (
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-montserrat font-extrabold text-xl text-gray-800">
                  Paquetes
                  {pagination && (
                    <span className="font-normal text-base text-gray-400 ml-2">
                      {pagination.total} resultado{pagination.total !== 1 ? 's' : ''}
                    </span>
                  )}
                </h2>
                {(filters.user_id || filters.provider_id || filters.user_ids?.length || filters.provider_ids?.length || filters.product_id || filters.item_quantity || filters.product_stock || filters.driver_ids?.length || filters.status || filters.delivery_status || filters.district || filters.date_from || filters.date_to || filters.delivery_date) && (
                  <p className="font-montserrat text-sm text-gray-500 mt-0.5">
                    Filtros activos
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Filters + scan button row */}
          <div className="flex flex-wrap gap-3 items-start">
            <div className="min-w-0 w-full xl:flex-1">
              <PackageFilters
                filters={filters}
                applyFilter={applyFilter}
                clearFilters={clearFilters}
                requestUsers={requestUsers}
                onUserSearch={setUserSearch}
                users={users}
                usersLoading={usersLoading}
                activeUsersOnly={activeUsersOnly}
                setActiveUsersOnly={setActiveUsersOnly}
                drivers={drivers}
                driversLoading={driversLoading}
                driversError={driversError}
                requestDrivers={requestDrivers}
                productCounts={productCounts}
                productCountsLoading={packagesLoading}
                onOpenProductCounts={() => setProductCountOpen(true)}
                returnDraft={returnDraft}
                onAddReturnPackage={addReturnPackage}
                onRemoveReturnPackage={id => setReturnDraft(prev => prev.filter(pkg => pkg.id !== id))}
                onPrepareReturnDraft={prepareReturnDraft}
              />
            </div>
            <button disabled={!dashboardData.warehouseConfig.enabled} title="Requiere stock conciliado y reservas habilitadas" onClick={() => applyFilter({ fulfillment_state: 'returns', delivery_date: '', status: '', delivery_status: '' })} className="min-w-0 flex-1 border rounded-xl p-3 text-sm xl:flex-none">Devoluciones pendientes</button>
            <button type="button" onClick={() => setUnbatchedOpen(true)} className="min-w-0 flex-1 border rounded-xl p-3 text-sm xl:flex-none">Escaneos sin lote</button>
            <button type="button" onClick={() => setReturnBatchesOpen(true)} className="min-w-0 flex-1 border rounded-xl p-3 text-sm xl:flex-none">Lotes devueltos</button>
            <button
              onClick={() => setScannerOpen(true)}
              className="flex flex-1 items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary/90 text-white font-montserrat font-semibold text-sm px-4 py-3 rounded-xl transition-colors shadow-sm xl:flex-none"
            >
              <ScanLine size={18} />
              <span>Escanear</span>
            </button>
          </div>

          {/* Error state */}
          {packagesError && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm font-montserrat text-red-600">
              {packagesError}
            </div>
          )}

          {/* Table card */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {packagesLoading && packages.length === 0 ? (
              <div className="flex items-center justify-center py-20 gap-3">
                <Loader2 size={24} className="animate-spin text-brand-primary" />
                <span className="font-montserrat text-gray-400 text-sm">Cargando paquetes...</span>
              </div>
            ) : packages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Package size={40} className="text-gray-200" />
                <p className="font-montserrat text-gray-400 text-sm">
                  {packagesError ? 'No se pudo actualizar la lista de paquetes' : 'Sin paquetes con los filtros actuales'}
                </p>
              </div>
            ) : (
              <>
              <div className="md:hidden divide-y divide-gray-100">
                <label className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-gray-600">
                  <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected; }} onChange={toggleAll} className="h-4 w-4 accent-brand-primary"/>
                  Seleccionar esta página
                </label>
                {packages.map(pkg => <PackageCard key={pkg.id} pkg={pkg} selected={isSelected(pkg.id)}
                  onToggle={toggleRow} onHistoryClick={setHistoryPackageId} onPrint={onPrint} onQueue={onQueue}
                  queued={isQueued(pkg)} onFulfillment={setFulfillmentPackage}/>)}
              </div>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {/* Select all checkbox */}
                      <th className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={el => { if (el) el.indeterminate = someSelected; }}
                          onChange={toggleAll}
                          className="w-4 h-4 accent-brand-primary cursor-pointer"
                        />
                      </th>
                      <Th>Entrega</Th>
                      <Th>Estado</Th>
                      <Th>Destinatario</Th>
                      <Th>Distrito</Th>
                      <Th>Tienda</Th>
                      <Th>Conductor</Th>
                      <Th>Actualizado</Th>
                      <Th>Acciones</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {packages.map(pkg => (
                      <PackageRow
                        key={pkg.id}
                        pkg={pkg}
                        selected={isSelected(pkg.id)}
                        onToggle={toggleRow}
                        onHistoryClick={setHistoryPackageId}
                        onPrint={onPrint} onQueue={onQueue} queued={isQueued(pkg)} onFulfillment={setFulfillmentPackage}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>

          <Pagination value={pagination} onPage={goToPage} onSize={per_page => applyFilter({ per_page })} loading={packagesLoading}/>
        </div>
      </div>

      {fulfillmentPackage && <FulfillmentPanel packageId={fulfillmentPackage} onClose={() => setFulfillmentPackage(null)} onSaved={fetchPackages}/>}

      {productCountOpen && <ProductCountModal counts={productCounts} error={productCountsError} loading={packagesLoading}
        sort={productSort} onSort={setProductSort} page={productPage} onPage={setProductPage}
        onClose={() => setProductCountOpen(false)}/>}

      {/* History side panel */}
      {historyPackageId && (
        <PackageHistoryPanel
          key={historyPackageId}
          packageId={historyPackageId}
          fetchPackageHistory={fetchPackageHistory}
          history={history}
          historyLoading={historyLoading}
          onClose={() => setHistoryPackageId(null)}
        />
      )}

      {/* Scanner modal */}
      {scannerOpen && (
        <ScannerModal
          userId={userId}
          onClose={() => setScannerOpen(false)}
          onSuccess={handleScanSuccess}
        />
      )}
      {returnSelection && <ReturnBatchModal
        packages={returnSelection}
        onSaved={() => { setSelectedIds([]); if (returnFromDraft) setReturnDraft([]); fetchPackages(); fetchStats(); }}
        onClose={() => setReturnSelection(null)}
      />}
      {unbatchedOpen && <UnbatchedReturnsModal onClose={() => setUnbatchedOpen(false)} />}
      <ReturnBatchesModal isOpen={returnBatchesOpen} onClose={() => setReturnBatchesOpen(false)} />
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-montserrat font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">
      {children}
    </th>
  );
}

function PackageCard({ pkg, selected, onToggle, onHistoryClick, onPrint, onQueue, queued, onFulfillment }) {
  const status = STATUS_CONFIG[pkg.status] || { label: pkg.status, color: 'bg-gray-100 text-gray-600' };
  return <article className={`min-w-0 p-4 ${selected ? 'bg-brand-primary/5' : ''}`}>
    <div className="flex min-w-0 items-start gap-3">
      <input type="checkbox" aria-label={`Seleccionar paquete ${pkg.id}`} checked={selected} onChange={() => onToggle(pkg.id)}
        className="mt-1 h-4 w-4 shrink-0 accent-brand-primary"/>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <strong className="text-sm text-brand-primary">#{pkg.delivery_point_id || pkg.delivery_point_provincia_id || pkg.id}{pkg.is_provincia ? ' · Provincia' : ''}</strong>
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${status.color}`}>{status.label}</span>
        </div>
        <p className="mt-1 break-words text-sm font-semibold text-gray-800">{pkg.recipient_name || 'Sin destinatario'}</p>
        <p className="break-words text-xs text-gray-500">{pkg.store_user?.username || 'Sin tienda'} · {pkg.district || 'Sin distrito'}</p>
        <p className="break-all text-xs text-gray-400">{pkg.tracking_code}</p>
        {!!pkg.items?.length && <div className="mt-2 rounded-lg bg-gray-50 p-2">
          {pkg.items.map((item, index) => <p key={`${item.product_sku}-${index}`} className="break-words text-xs text-gray-600">{formatPackageItem(item)}</p>)}
        </div>}
        <p className="mt-2 text-xs text-gray-500">Conductor: {pkg.driver?.username || 'Sin asignar'}</p>
        <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-gray-100 pt-2">
          <PrintActions pkg={pkg} onPrint={onPrint} onQueue={onQueue} queued={queued}/>
          <button type="button" aria-label="Preparación y devoluciones" onClick={() => onFulfillment(pkg.id)} className="rounded p-2 text-gray-600 hover:bg-gray-100"><Package size={17}/></button>
          <button type="button" aria-label="Historial" onClick={() => onHistoryClick(pkg.id)} className="rounded p-2 text-gray-600 hover:bg-gray-100"><History size={17}/></button>
        </div>
      </div>
    </div>
  </article>;
}

function PackageRow({ pkg, selected, onToggle, onHistoryClick, onPrint, onQueue, queued, onFulfillment }) {
  const cfg = STATUS_CONFIG[pkg.status] || { label: pkg.status, color: 'bg-gray-100 text-gray-600' };
  const [showProducts, setShowProducts] = useState(false);
  const productItems = (pkg.items || []).filter(item => item.product_title || item.product_sku);
  const firstProduct = productItems[0];
  const firstProductLabel = firstProduct ? formatPackageItem(firstProduct) : '';
  const updatedAt = pkg.updated_at
    ? new Date(pkg.updated_at).toLocaleString('es-PE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';

  return (
    <tr className={`transition-colors ${selected ? 'bg-brand-primary/5' : 'hover:bg-gray-50/60'}`}>
      <td className="px-4 py-3">
        <input type="checkbox" checked={selected} onChange={() => onToggle(pkg.id)}
          className="w-4 h-4 accent-brand-primary cursor-pointer" />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="font-montserrat font-extrabold text-sm text-brand-primary">
            #{pkg.delivery_point_id || pkg.delivery_point_provincia_id || '—'}
          </span>
          {pkg.is_provincia && (
            <span className="text-xs bg-purple-100 text-purple-600 font-montserrat font-semibold px-1.5 py-0.5 rounded">
              Prov
            </span>
          )}
        </div>
        {firstProduct && (
          <div className="mt-0.5 max-w-[220px]">
            <button
              type="button"
              onClick={() => setShowProducts(prev => !prev)}
              aria-expanded={showProducts}
              className="font-montserrat text-xs text-gray-400 hover:text-brand-primary text-left truncate max-w-[140px] block"
            >
              {firstProductLabel}
              {productItems.length > 1 && (
                <span className="ml-1 text-[10px] font-semibold text-brand-primary">
                  +{productItems.length - 1}
                </span>
              )}
            </button>
            {showProducts && (
              <div className="mt-1 rounded-lg border border-gray-100 bg-gray-50 px-2 py-1.5 max-h-28 overflow-y-auto">
                {productItems.map((item, index) => (
                  <p key={`${item.product_sku || item.product_title}-${index}`} className="font-montserrat text-xs text-gray-600 leading-snug">
                    {formatPackageItem(item)}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
        <p className="font-montserrat text-xs text-gray-300 truncate max-w-[140px]">{pkg.tracking_code}</p>
      </td>
      <td className="px-4 py-3">
        <span className={`text-xs px-2.5 py-1 rounded-full font-montserrat font-semibold whitespace-nowrap ${cfg.color}`}>
          {cfg.label}
        </span>
        {pkg.fulfillment_state && <p className="text-xs text-gray-500 mt-1">{fulfillmentLabels[pkg.fulfillment_state] || pkg.fulfillment_state}</p>}
      </td>
      <td className="px-4 py-3">
        <span className="font-montserrat text-sm text-gray-700 flex items-center gap-1">
          <User size={12} className="text-gray-300 shrink-0" />{pkg.recipient_name || '—'}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="font-montserrat text-sm text-gray-500 flex items-center gap-1">
          <MapPin size={12} className="text-gray-300 shrink-0" />{pkg.district || '—'}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="font-montserrat text-sm text-gray-500">{pkg.store_user?.username || '—'}</span>
      </td>
      <td className="px-4 py-3">
        {pkg.driver
          ? <span className="font-montserrat text-sm text-gray-500 flex items-center gap-1"><Truck size={12} className="text-gray-300 shrink-0" />{pkg.driver.username}</span>
          : <span className="text-gray-300 text-sm">—</span>}
      </td>
      <td className="px-4 py-3">
        <span className="font-montserrat text-xs text-gray-400 whitespace-nowrap">{updatedAt}</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center">
        <PrintActions pkg={pkg} onPrint={onPrint} onQueue={onQueue} queued={queued}/>
        <button title="Preparacion y devoluciones" aria-label="Preparacion y devoluciones" onClick={() => onFulfillment(pkg.id)} className="p-2 hover:bg-gray-100 rounded"><Package size={16}/></button>
        <button title="Historial" aria-label="Historial" onClick={() => onHistoryClick(pkg.id)}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-300 hover:text-brand-primary hover:bg-brand-primary/10 transition-colors">
          <History size={16} />
        </button>
        </div>
      </td>
    </tr>
  );
}

function formatPackageItem(item) {
  const quantity = Number(item.quantity) || 1;
  const title = item.product_title || item.product_sku || 'Producto';
  return `x${quantity} ${title}`;
}
